/**
 * 조건 요약 버튼 — 넓은 화면은 [필터 · 「10 · 선택형 +1 · 섞기」 · ⌄] 글자 버튼, 좁은 화면(시트)은 필터 아이콘만 있는
 * ZUS IconButton(자료 버튼과 같은 크기). 메뉴(Dropdown.Trigger)와 시트(DrawerTrigger)가 asChild 로 감싸 열림·ref 를 넘긴다.
 * 글자 버튼은 회색 판(bg-alternative) 위에 hover 막만 얹고, 아이콘 버튼은 판 없이 transparent 그대로 쓴다(Figma 6-2).
 * 지금은 좁은 화면 시트(iconOnly)만 쓴다 — 넓은 화면은 AiGenerateDockConditionsMenu 의 칩 3개가 맡는다.
 */
import { forwardRef } from 'react';
import { Button, type ButtonProps, IconButton } from '@zep-us/zus/button';
import { ICON_COLOR } from '@zep-us/zus/tokens';
import { ChevronDownIcon, FilterIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';

/** 회색 판을 지킨 채 transparent/neutral 의 hover 막(opacity-black-1)을 위에 얹는다. 열린 상태엔 막을 두지 않는다. */
const TRIGGER_SURFACE_CLASS = cn('bg-alternative hover:bg-alternative', 'hover:[background-image:linear-gradient(theme(colors.opacity.black.1),theme(colors.opacity.black.1))]');
export type AiGenerateDockConditionsTriggerProps = Omit<ButtonProps, 'children' | 'leftSlot' | 'rightSlot' | 'asChild'> & {
  /** 「조건」 — aria-label 앞부분 */
  label: string;
  summary: string;
  /** 좁은 화면 — 필터 아이콘만 있는 정사각 버튼 */
  iconOnly?: boolean;
};
export const AiGenerateDockConditionsTrigger = forwardRef<HTMLButtonElement, AiGenerateDockConditionsTriggerProps>(({
  label,
  summary,
  iconOnly = false,
  className,
  ...rest
}, ref) => {
  const common = {
    ref,
    variant: 'transparent',
    intent: 'neutral',
    'aria-label': `${label}: ${summary}`,
    'data-testid': 'ai-generate-dock-conditions-toggle'
  } as const;
  if (iconOnly) {
    return <IconButton {...common} size="sm"
    // 좁은 화면 필터 버튼은 판 없이 아이콘만 — 넓은 화면 조건 칩(transparent)과 같은 결(10/23 지민, Figma 6-2).
    className={cn('shrink-0', className)} {...rest}>
        <FilterIcon size="xs" fill={ICON_COLOR.neutral} aria-hidden />
      </IconButton>;
  }
  return <Button {...common} size="sm" className={cn('min-w-0 max-w-full shrink px-sm text-body-sm-semibold text-neutral', TRIGGER_SURFACE_CLASS, className)} leftSlot={<FilterIcon size="xs" fill={ICON_COLOR.neutral} className="shrink-0" aria-hidden />} rightSlot={<ChevronDownIcon size="xs" fill={ICON_COLOR.neutral} className="shrink-0" aria-hidden />} {...rest}>
      <span className="truncate">{summary}</span>
    </Button>;
});
AiGenerateDockConditionsTrigger.displayName = 'AiGenerateDockConditionsTrigger';